import { FC } from 'react';
import { Modal, Button, Tag, Space } from 'antd';
import { MailOutlined, UserOutlined, WarningOutlined } from '@ant-design/icons';

export interface EmailRecipient {
  email: string;
  name: string;
  role?: string;
}

interface EmailConfirmationModalProps {
  open: boolean;
  recipients: EmailRecipient[];
  onConfirm: (sendEmails: boolean) => void;
  onCancel: () => void;
  actionDescription: string; // e.g., "Staff Assignment" or "@Mention in Note"
  loading?: boolean;
}

export const EmailConfirmationModal: FC<EmailConfirmationModalProps> = ({
  open,
  recipients,
  onConfirm,
  onCancel,
  actionDescription,
  loading = false,
}) => {
  const handleProceedWithEmails = () => {
    onConfirm(true);
  };

  const handleProceedWithoutEmails = () => {
    onConfirm(false);
  };

  return (
    <Modal
      open={open}
      title={
        <div className="flex items-center gap-2">
          <MailOutlined className="text-blue-600" />
          <span>Email Notification Confirmation</span>
        </div>
      }
      footer={null}
      onCancel={onCancel}
      width={550}
    >
      <div className="py-4">
        {/* Action Description */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <p className="text-sm text-blue-900">
            <strong>Action:</strong> {actionDescription}
          </p>
        </div>

        {/* Warning */}
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded flex items-start gap-2">
          <WarningOutlined className="text-amber-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-900 font-medium mb-1">
              Email notifications will be sent
            </p>
            <p className="text-xs text-amber-700">
              The following people will receive email notifications about this action.
              Choose whether to send emails or proceed without notifications.
            </p>
          </div>
        </div>

        {/* Recipients List */}
        <div className="mb-6">
          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
            <UserOutlined /> Recipients ({recipients.length})
          </h4>
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded">
            <div className="divide-y">
              {recipients.map((recipient, index) => (
                <div
                  key={index}
                  className="p-3 hover:bg-gray-50 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">{recipient.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <MailOutlined className="text-xs" />
                      {recipient.email}
                    </div>
                  </div>
                  {recipient.role && (
                    <Tag color="blue" className="text-xs">
                      {recipient.role}
                    </Tag>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
          <p className="text-xs text-gray-600">
            <strong>Note:</strong> Regardless of your choice, the action will complete
            (staff will be assigned, note will be saved, etc.). This only controls
            whether email notifications are sent.
          </p>
        </div>

        {/* Action Buttons */}
        <Space className="w-full flex justify-end" size="middle">
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleProceedWithoutEmails} disabled={loading}>
            Proceed Without Emails
          </Button>
          <Button
            type="primary"
            icon={<MailOutlined />}
            onClick={handleProceedWithEmails}
            loading={loading}
          >
            Send Emails & Proceed
          </Button>
        </Space>
      </div>
    </Modal>
  );
};
